import { computed, inject } from '@angular/core';
import { patchState, signalStore, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom, forkJoin } from 'rxjs';

import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { withSelectedEntity, withSelectedEntities } from './with-selected-entities.feature';
import { setFulfilled, setPending, withRequestStatus, withSyncToWebStorage } from '@shared/stores/store-features';

import { LocationService } from '@core/services/location.service';
import { ActivityService } from '@core/services/activity.service';
import { ResourceService } from '@core/services/resource.service';
import { Activity } from '@shared/models/activity';
import { Location } from '@shared/models/location';
import { Resource } from '@shared/models/resource';
import { DateService } from '@core/services/date.service';

import {
  SearchResponse,
  SearchQueryParams,
  id_SearchResponseTimeslot_Convention,
} from '@features/associations/models/search-response';
import { SearchService } from '@features/associations/search.service';
import { CalendarDay } from '@shared/models/dateTypes';

// DateService has no constructor dependencies, so a plain instance is safe and correct.
const dateService = new DateService();

/**
 * Keep an eye on this repo for extensions
 * @ngrx-traits/signals
 */
export interface SearchStoreState {
  activity: Activity;
  location: Location;
  locationResources: Resource[];
  selectedStart: CalendarDay | null;
  selectedEnd: CalendarDay | null;
  searchCount: number;
}

export const SearchStore = signalStore(
  { providedIn: 'root' }, // 👇 Providing `SearchStore` at the root level.
  withEntities<SearchResponse>(),
  withRequestStatus(),
  withSelectedEntity(),
  withSelectedEntities(),
  signalStoreFeature(
    withState<SearchStoreState>({
      activity: null,
      location: null,
      locationResources: null,
      selectedStart: null,
      selectedEnd: null,
      searchCount: 0,
    }),
  ),
  withSyncToWebStorage({
    key: 'abook-search-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
    // optionally, filter the state before saving to the storage
    filterState: ({ location, activity, locationResources, selectedStart, selectedEnd }) => ({
      location,
      activity,
      locationResources,
      selectedStart,
      selectedEnd,

      // AS OF NOW WE DO NOT STORE THE SEARCH RESPONSE DATA
    }),
  }),
  withComputed((store) => ({
    // if no ids then no entities
    hasNoResults: computed(() => store.ids().length === 0),

    // update url when start and end dates changes ???

    getSearchQueryParams: computed((): SearchQueryParams => {
      return {
        locationId: store.location()?.id,
        activityId: store.activity()?.id,
        start: store.selectedStart(),
        end: store.selectedEnd(),
      };
    }),
  })),

  withMethods((store) => {
    const searchService = inject(SearchService);
    const locationService = inject(LocationService);
    const activityService = inject(ActivityService);
    const resourceService = inject(ResourceService);
    // Bumped by every search and every clear: only the latest search may fill the store, so one
    // that answers after a newer search, or after the band was cleared, is dropped.
    let latestSearch = 0;

    return {
      // getSelectedEntity(): Observable<number> {
      //   return this.selectedEntityId();
      // },

      updateSelectedDate(start: CalendarDay, end: CalendarDay) {
        if (!start || !end) {
          return;
        }

        patchState(store, { selectedStart: start, selectedEnd: end });
      },

      updateActivity(activity: Activity) {
        patchState(store, { activity });
      },

      updateLocation(location: Location) {
        patchState(store, { location });
      },

      clearAllFilters() {
        latestSearch++;
        patchState(store, setFulfilled(), {
          activity: null,
          location: null,
          locationResources: null,
          selectedStart: null,
          selectedEnd: null,
          searchCount: 0,
        });
        patchState(store, setAllEntities([]));
      },

      getResourceById(id: number) {
        // Requires locationResources to be avaiable in store
        // TO DO : Might need an api call if it is not found
        return store.locationResources().find((resource) => resource.id === id);
      },

      // PROMISE based API EXAMPLE
      async search(locationId: number, activityId: number, start: CalendarDay, end: CalendarDay): Promise<string> {
        if (!locationId || locationId == 0) return Promise.reject('locationId is required to perform search');

        // Denmark's today, not the browser's — against the browser's own day this rejects a
        // legitimate search for today from every timezone west of Copenhagen, and accepts a stale
        // one from the east. Two calendar days compare directly; no instant is involved either
        // side, so there is no midnight to anchor.
        const today = dateService.toDateParam(new Date());

        if (start < today) return Promise.reject('start date must be later than now ');

        const search = ++latestSearch;
        patchState(store, setPending());

        // Awaited, so the promise settles once the results are in the store: the page reads
        // hasNoResults() when it resolves, and before that it would still see the previous search.
        const [searchResponses, location, activity, locationResources] = await firstValueFrom(
          forkJoin([
            searchService.search({ locationId, activityId, start, end }),
            locationService.get(locationId, true),
            activityService.get(activityId, true),
            resourceService.getList(locationId, true),
          ]),
        );

        if (search !== latestSearch) return 'Search superseded';

        // Mutations from the API model to the store model: each timeslot gets its id.
        const mutatedSearchResponses = searchResponses.map((searchResponse) => ({
          ...searchResponse,
          timeslots: searchResponse.timeslots.map((timeslot) => ({
            ...timeslot,
            id: id_SearchResponseTimeslot_Convention(
              timeslot.locationId,
              timeslot.resourceId,
              timeslot.start,
              timeslot.end,
            ),
          })),
        })) as SearchResponse[];

        patchState(
          store,
          setAllEntities(mutatedSearchResponses),
          (state) => ({
            location,
            activity,
            locationResources,
            selectedStart: start,
            selectedEnd: end,
            searchCount: state.searchCount + 1,
          }),
          setFulfilled(),
        );

        return 'Search completed';
      },
    };
  }),
);
